<template>
  <div
    class="flex min-h-10 w-full flex-wrap items-center rounded-sm border p-1 text-sm leading-7 text-headline"
  >
    <lf-badge
      v-for="(entry, index) in validEmails"
      :key="entry"
      removable
      is-rounded
      :class="{
        'bg-woods-green/10 text-woods-green': isDynamicField(entry),
      }"
      @click:remove="removeEmail(index)"
    >
      {{ dynamicFieldLabels.get(entry) ?? entry }}
    </lf-badge>
    <div class="relative min-w-[50%] grow">
      <input
        ref="inputRef"
        v-model="inputValue"
        type="text"
        class="w-full border-0 px-2 whitespace-pre placeholder:text-silver focus:border-0 focus:border-transparent focus:ring-0 focus:outline-hidden"
        :class="{ 'rounded-sm border border-disabled': !!validEmails.length }"
        data-cy="email-input-multiple"
        :placeholder="t('EMAIL_INTEGRATION.TYPE_EMAIL')"
        @input="handleContentChange"
        @blur="handleBlur"
        @keydown="handleKeydown"
      />
      <ul
        v-if="showDropdown && filteredFields.length"
        data-cy="dynamic-fields-dropdown"
        class="ql-mention-list absolute top-full left-0 mt-1"
      >
        <li
          v-for="(field, index) in filteredFields"
          :key="field.tag"
          class="ql-mention-list-item text-sm"
          :class="{ selected: index === highlightedIndex }"
          @mousedown.prevent="selectDynamicField(field)"
        >
          {{ field.name }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import uniq from "lodash/uniq";

import { useNotification } from "@/hooks/notifications";

import type { DynamicFieldOption } from "@/models/options";

const props = defineProps<{
  value: string | null;
  emails: string[];
  dynamicFields?: DynamicFieldOption[];
}>();

const emit = defineEmits<{
  change: [{ validEmails: string[]; input: string }];
}>();

const { showMessage } = useNotification();
const { t } = useI18n();

const validEmails = ref<string[]>(props.emails || []);
const inputValue = ref<string>(props.value || "");
const showDropdown = ref(false);
const highlightedIndex = ref(0);
const inputRef = ref<HTMLInputElement | null>(null);

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const dynamicFieldRegex = /^\[.+\]$/;

const isValidEntry = (value: string) =>
  emailRegex.test(value) || dynamicFieldRegex.test(value);

const isDynamicField = (value: string) => dynamicFieldRegex.test(value);

const dynamicFieldLabels = computed(() => {
  const map = new Map<string, string>();
  if (!props.dynamicFields) {
    return map;
  }
  for (const field of props.dynamicFields) {
    map.set(field.tag, field.name);
  }
  return map;
});

const filteredFields = computed(() => {
  if (!props.dynamicFields?.length) {
    return [];
  }
  const bracketIndex = inputValue.value.lastIndexOf("[");
  const search =
    bracketIndex !== -1
      ? inputValue.value.slice(bracketIndex + 1).toLowerCase()
      : "";
  return props.dynamicFields.filter((field) =>
    field.name.toLowerCase().includes(search)
  );
});

const handleContentChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const input = target.value;

  const shouldCheckForEmails = input.trimEnd().endsWith(",");
  if (shouldCheckForEmails) {
    showDropdown.value = false;
    processInput(input);
    return;
  }

  if (input.endsWith("[") && props.dynamicFields?.length) {
    showDropdown.value = true;
    highlightedIndex.value = 0;
    return;
  }

  if (showDropdown.value) {
    if (!input.includes("[")) {
      showDropdown.value = false;
    }
    highlightedIndex.value = 0;
    return;
  }

  showDropdown.value = false;
};

const handleBlur = () => {
  showDropdown.value = false;
  const input = inputValue.value.trim();
  if (!input) {
    emit("change", { validEmails: validEmails.value, input: "" });
    return;
  }
  processInput(input);
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!showDropdown.value) {
    return;
  }

  if (!filteredFields.value.length) {
    if (event.key === "Escape" || event.key === "Enter") {
      event.preventDefault();
      showDropdown.value = false;
    }
    return;
  }

  if (event.key === "ArrowDown") {
    event.preventDefault();
    highlightedIndex.value = Math.min(
      highlightedIndex.value + 1,
      filteredFields.value.length - 1
    );
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
  } else if (event.key === "Enter") {
    event.preventDefault();
    selectDynamicField(filteredFields.value[highlightedIndex.value]);
  } else if (event.key === "Escape") {
    showDropdown.value = false;
  }
};

const selectDynamicField = (field: DynamicFieldOption) => {
  const tag = field.tag;

  const bracketIndex = inputValue.value.lastIndexOf("[");
  if (bracketIndex > 0) {
    const beforeBracket = inputValue.value.slice(0, bracketIndex).trim();
    if (beforeBracket) {
      processInput(beforeBracket);
    }
  }

  if (validEmails.value.includes(tag)) {
    showMessage(t("EMAIL_INTEGRATION.UNIQUE_EMAIL_WARNING"), "warning");
  } else {
    validEmails.value = [...validEmails.value, tag];
  }
  inputValue.value = "";
  showDropdown.value = false;
  emit("change", { validEmails: validEmails.value, input: "" });
  inputRef.value?.focus();
};

const processInput = (input: string) => {
  const entries = input.split(",").map((entry) => entry.trim());

  const addedEntries = [
    ...validEmails.value,
    ...entries.filter((entry) => isValidEntry(entry)),
  ];

  if (new Set(addedEntries).size !== addedEntries.length) {
    showMessage(t("EMAIL_INTEGRATION.UNIQUE_EMAIL_WARNING"), "warning");
  }

  validEmails.value = uniq(addedEntries);
  const invalidEntries = entries
    .filter((entry) => entry && !isValidEntry(entry))
    .join(", ");
  inputValue.value = invalidEntries;
  emit("change", { validEmails: validEmails.value, input: invalidEntries });
};

const removeEmail = (index: number) => {
  validEmails.value.splice(index, 1);
  emit("change", { validEmails: validEmails.value, input: inputValue.value });
};
</script>
